import { Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AccountActions, PixActions } from '@portal/actions';
import { pix } from '@portal/config/app.json';
import { translate } from '@portal/services/i18n';
import { moneyFormat } from '@portal/utils/format';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import useReduxState from '@portal/hooks/useReduxState';
import { AccountStatus } from '@portal/models/enumerators';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { getPaymentAccountByType } from '@portal/utils/paymentAccount';

type IProps = {
  visible: boolean;
  onCloseModal: () => void;
};

const CloseAccountModal: React.FC<IProps> = ({
  visible,
  onCloseModal,
}: IProps) => {
  const {
    pix: { pixKeys },
    account: { balance },
    user: { me: loggedUser },
  } = useReduxState();

  const dispatch = useDispatch();
  const [comment, setComment] = useState('');

  const handleCloseModal = () => {
    onCloseModal();
    setComment('');
  };

  const handleConfirm = () => {
    dispatch(AccountActions.requestClosure(comment));
    handleCloseModal();
  };

  const isDisabled = (): boolean => {
    if (!paymentAccount) return false;

    if (balance === null) return true;
    if (Number(balance.available) > 0) return true;
    if (pix.enabled && Array.isArray(pixKeys) && pixKeys.length > 0)
      return true;
    return false;
  };

  const paymentAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.ACCOUNT
  );

  useEffect(() => {
    if (pix.enabled && paymentAccount) {
      dispatch(PixActions.getAllPixKeys());
    }
    if (paymentAccount) {
      dispatch(AccountActions.getUserBalance());
    }
  }, [dispatch, paymentAccount]);

  const isClosureRequested =
    loggedUser.account.status === AccountStatus.CLOSURE_REQUESTED;

  return (
    <Modal
      title={translate(
        isClosureRequested
          ? 'COMPONENTS.CLOSE_ACCOUNT_MODAL.TITLE_PENDING'
          : 'COMPONENTS.CLOSE_ACCOUNT_MODAL.TITLE'
      )}
      open={visible}
      onCancel={() => handleCloseModal()}
      footer={
        isClosureRequested
          ? null
          : [
              <div className="close-account-modal__footer" key={'confirm'}>
                <AdvancedButton
                  key="upload"
                  text={translate(
                    'COMPONENTS.CLOSE_ACCOUNT_MODAL.BUTTON.CONFIRM'
                  )}
                  onClick={() => handleConfirm()}
                  disabled={isDisabled()}
                />
              </div>,
            ]
      }
    >
      <div className="close-account-modal">
        {isClosureRequested ? (
          <p>{translate('COMPONENTS.CLOSE_ACCOUNT_MODAL.TEXT_PENDING')}</p>
        ) : (
          <>
            <p>
              <h4>{translate('COMPONENTS.CLOSE_ACCOUNT_MODAL.TEXT')}</h4>
              <br />
              <b>
                {`${translate(
                  'COMPONENTS.CLOSE_ACCOUNT_MODAL.LABEL.BALANCE'
                )}: `}
              </b>
              {balance
                ? moneyFormat(Number(balance.available))
                : moneyFormat(0)}
              <br />
              {pix.enabled ? (
                <>
                  <b>
                    {`${translate(
                      'COMPONENTS.CLOSE_ACCOUNT_MODAL.LABEL.PIX_KEYS'
                    )}: `}
                  </b>
                  {pixKeys ? pixKeys.length : 0}
                </>
              ) : null}
            </p>
            <AdvancedInput
              multiline
              value={comment}
              onChange={(newComment: string) => setComment(newComment)}
              placeholder={translate(
                'COMPONENTS.CLOSE_ACCOUNT_MODAL.LABEL.PLACEHOLDER'
              )}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

export default CloseAccountModal;
